// src/components/AdminDashboard/sections/UserRolesSection.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  MenuItem,
  CircularProgress,
  Box,
  Button,
  Paper,
  FormControl,
  Select
} from '@mui/material';
// Removed styled-components import - now using Tailwind classes
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';

function UserRolesSection({
  users,
  loadingUsers,
  selectedUser,
  selectedRole,
  setSelectedUser,
  setSelectedRole,
  handleAssignRole,
  loadingAssign,
  assignError,
  assignSuccess
}) {
  return (
    <Paper className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 mb-8 shadow-admin-card border border-accent-teal/10 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-admin-card-hover hover:border-accent-teal/20 relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-0.5 before:bg-gradient-to-r before:from-accent-teal before:to-accent-orange before:opacity-0 before:transition-opacity before:duration-300 hover:before:opacity-100">
      <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-accent-teal/10">
        <Box display="flex" alignItems="center" gap={1}>
          <SecurityIcon className="text-accent-teal" />
          <Typography variant="h6" className="text-accent-grey font-bold text-xl m-0">
            User Roles
          </Typography>
        </Box>
      </div>
      
      <form onSubmit={handleAssignRole} className="flex flex-col gap-6 max-w-2xl p-6 bg-gradient-to-br from-accent-teal/5 to-accent-orange/5 rounded-xl border border-accent-teal/10">
        <FormControl variant="outlined" required className="[&_.MuiOutlinedInput-root]:rounded-lg [&_.MuiOutlinedInput-root]:bg-white [&_.MuiOutlinedInput-root]:transition-all [&_.MuiOutlinedInput-root]:duration-300 [&_.MuiOutlinedInput-root:hover]:border-accent-teal [&_.MuiOutlinedInput-root.Mui-focused]:border-accent-teal [&_.MuiOutlinedInput-root.Mui-focused]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)] [&_.MuiInputLabel-root]:text-accent-grey [&_.MuiInputLabel-root]:font-semibold [&_.MuiInputLabel-root.Mui-focused]:text-accent-teal">
          <Select
            labelId="select-user-label"
            value={selectedUser}
            label="Select User"
            onChange={(e) => setSelectedUser(e.target.value)}
            className="[&_.MuiOutlinedInput-notchedOutline]:border-accent-grey/30 [&_.MuiOutlinedInput-notchedOutline]:rounded-lg hover:[&_.MuiOutlinedInput-notchedOutline]:border-accent-teal focus:[&_.MuiOutlinedInput-notchedOutline]:border-accent-teal focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
          >
            {loadingUsers ? (
              <MenuItem value="">
                <em>Loading...</em>
              </MenuItem>
            ) : (
              users && Array.isArray(users) && users.map((user) => (
                <MenuItem key={user.id} value={user.username}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon className="text-base" />
                    {user.username} ({user.email}) - Current Role: {user.roles.join(', ')}
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <FormControl variant="outlined" required className="[&_.MuiOutlinedInput-root]:rounded-lg [&_.MuiOutlinedInput-root]:bg-white [&_.MuiOutlinedInput-root]:transition-all [&_.MuiOutlinedInput-root]:duration-300 [&_.MuiOutlinedInput-root:hover]:border-accent-teal [&_.MuiOutlinedInput-root.Mui-focused]:border-accent-teal [&_.MuiOutlinedInput-root.Mui-focused]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)] [&_.MuiInputLabel-root]:text-accent-grey [&_.MuiInputLabel-root]:font-semibold [&_.MuiInputLabel-root.Mui-focused]:text-accent-teal">
          <Select
            labelId="select-role-label"
            value={selectedRole}
            label="Select Role"
            onChange={(e) => setSelectedRole(e.target.value)}
            className="[&_.MuiOutlinedInput-notchedOutline]:border-accent-grey/30 [&_.MuiOutlinedInput-notchedOutline]:rounded-lg hover:[&_.MuiOutlinedInput-notchedOutline]:border-accent-teal focus:[&_.MuiOutlinedInput-notchedOutline]:border-accent-teal focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
          >
            <MenuItem value="ADMIN">
              <Box display="flex" alignItems="center" gap={1}>
                <SecurityIcon className="text-base" />
                Admin
              </Box>
            </MenuItem>
            <MenuItem value="USER">
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon className="text-base" />
                User
              </Box>
            </MenuItem>
            <MenuItem value="SITE_OWNER">
              <Box display="flex" alignItems="center" gap={1}>
                <SecurityIcon className="text-base" />
                Site Owner
              </Box>
            </MenuItem>
            {/* Additional roles if needed */}
          </Select>
        </FormControl>

        <Button
          type="submit"
          variant="contained"
          disabled={loadingAssign}
          className="bg-gradient-to-r from-secondary to-accent-orange text-white rounded-lg px-8 py-4 font-bold normal-case shadow-lg transition-all duration-300 hover:from-accent-orange hover:to-secondary hover:-translate-y-0.5 hover:shadow-xl disabled:from-gray-300 disabled:to-gray-400 disabled:text-gray-500 disabled:transform-none disabled:shadow-none w-full md:w-auto md:px-8 md:py-4"
        >
          {loadingAssign ? <CircularProgress size={24} color="inherit" /> : 'Assign Role'}
        </Button>
        
        {assignError && (
          <div className="text-center text-red-500 mt-5 p-4 bg-gradient-to-br from-red-500/10 to-red-600/10 rounded-lg border-l-4 border-red-500 font-semibold">
            {assignError}
          </div>
        )}
        
        {assignSuccess && (
          <div className="text-center text-green-600 mt-5 p-4 bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-lg border-l-4 border-green-500 font-semibold">
            {assignSuccess}
          </div>
        )}
      </form>
    </Paper>
  );
}

UserRolesSection.propTypes = {
  users: PropTypes.array.isRequired,
  loadingUsers: PropTypes.bool.isRequired,
  selectedUser: PropTypes.string.isRequired,
  selectedRole: PropTypes.string.isRequired,
  setSelectedUser: PropTypes.func.isRequired,
  setSelectedRole: PropTypes.func.isRequired,
  handleAssignRole: PropTypes.func.isRequired,
  loadingAssign: PropTypes.bool.isRequired,
  assignError: PropTypes.string,
  assignSuccess: PropTypes.string
};

export default UserRolesSection;
